import { Drawer, Pagination, Table } from '@/components/commons';
import Form from '@/components/commons/Form';
import { DeleteContent } from '@/components/commons/DeleteContent';
import { ChatFukumaruService } from '@/services/chatFukumaru';
import { ENDPOINT } from '@/services/endpoint';
import { ImageService } from '@/services/image';
import { IResponse, IResponseError } from '@/types/common';
import {
  ICreateChatFukumaruRequest,
  IUpdateChatFukumaruRequest,
} from '@/types/request/chatFukumaru';
import { IUploadImageRequest } from '@/types/request/upload';
import {
  IGetDetailChatFukumaruResponse,
  IGetListChatFukumaruResponse,
} from '@/types/response/chatFukumaru';
import {
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  notificationStatus,
  NOTIFY_STATUS,
  Status,
} from '@/utils';
import {
  ERROR,
  SUCCESS,
  SUCCESS_MESSAGE,
} from '@/utils/constants/messages';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TableColumnsType } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { ChatFukumaruForm } from '../Drawers';
import { FORM_NAME } from '../Drawers/ChatFukumaruForm/constants';
import { formSchemaFn } from '../Drawers/ChatFukumaruForm/formSchemaFn';

interface IProps {
  title?: string;
}

interface IParams {
  limit: number;
  page: number;
}

export const ChatFukumaruList: React.FC<IProps> = ({ title }) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailChatFukumaruResponse>();
  const [isNotPermissionEdit, setIsNotPermissionEdit] =
    useState<boolean>(false);
  const [isNotFoundDetail, setIsNotFoundDetail] = useState<boolean>(false);
  const [params, setParams] = useState<IParams>({
    limit: 10,
    page: 1,
  });

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.CHAT_FUKUMARU.GET_LIST, params],
    () => ChatFukumaruService.getList(convertRequest(params)),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId, isNotPermissionEdit)),
    reValidateMode: 'onChange',
    defaultValues: {
      status: Status.DRAFT,
    },
  });

  const { handleSubmit, reset, watch, setValue, setError } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({ message: null });
  };

  const handleCreate = async (values: ICreateChatFukumaruRequest) => {
    const { attachments, isDisabled, ...rest } = values;
    const payload: ICreateChatFukumaruRequest = {
      ...rest,
      image: null,
      attachments: null,
      status: values.status,
    };
    try {
      if (values?.attachments?.fileList?.length) {
        const response = await ImageService.uploadFile(values?.attachments?.fileList[0]?.originFileObj);
        payload.image = response?.data?.url;
      }
      await createChatFukumaru(payload);
    } catch (error) {
      console.log({ error });
    }
  };

  const { mutate: createChatFukumaru, isLoading: isLoadingCreate } =
    useMutation(
      (body: ICreateChatFukumaruRequest) =>
        ChatFukumaruService.create(body),
      {
        onSuccess: () => {
          notificationMessage({
            message:
              SUCCESS_MESSAGE[
              SUCCESS.CREATE_CHAT_FUKUMARU as keyof typeof SUCCESS_MESSAGE
              ],
            type: NOTIFY_STATUS.SUCCESS,
          });
          refetch();
          handleCancel();
          setParams({ ...params, page: 1 });
        },
        onError: (response: IResponseError) => {
          handleErrorCommon(response, setError);
        },
      },
    );

  const { mutateAsync: uploadFile, isLoading: isLoadingUpload } = useMutation(
    (file: File) => ImageService.uploadFile(file),
    {
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.CHAT_FUKUMARU.GET_DETAIL.replace(':id', String(currentId))],
    () => ChatFukumaruService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (response: IResponse<IGetDetailChatFukumaruResponse>) => {
        const data = response?.data;
        setDetail(data);
        reset({
          formName: FORM_NAME.UPDATE_CHAT_FUKUMARU,
          title: data?.title || '',
          message: data?.message || '',
          image: data?.image?.url,
          startAt: moment(data?.startAt),
          status: data?.status,
          attachments: null,
          sourceType: data?.sourceType,
          sourceId: data?.sourceId,
          prevStatus: data?.status,
          publicAt: data?.publicAt ? moment(data?.publicAt) : null,
          isDisabled: data?.publicAt,
          url: data?.url || '',
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const handleUpdate = async (values: IUpdateChatFukumaruRequest) => {
    try {
      if (!detail) throw new Error('ChatFukumaru not found');
      const { attachments, isDisabled, ...rest } = values;
      const payload: IUpdateChatFukumaruRequest = {
        ...rest,
        image: null,
        status: values?.status ? 1 : 0,
        prevStatus: detail.status,
      };
      delete payload.attachments;
      delete payload.formName;
      if (values?.attachments?.fileList?.length) {
        const response = await ImageService.uploadFile(values?.attachments?.fileList[0]?.originFileObj);
        payload.image = response?.data?.url;
      } else if (values?.image) {
        payload.image = values.image;
      } else {
        payload.isRemoveImage = true;
      }
      await updateChatFukumaru({ body: payload, id: Number(currentId) });
    } catch (error) {
      console.log({ error });
    }
  };

  const handleDelete = (data: IGetDetailChatFukumaruResponse) => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      content: <DeleteContent name='タイトル' content={data?.title || ''} />,
      maskClosable: false,
      onOk: () => deleteChatFukumaru(currentId!),
      okText: '削除する',
      cancelText: 'キャンセル',
    });
  };

  const { mutate: updateChatFukumaru, isLoading: isLoadingUpdate } =
    useMutation(
      (payload: { body: IUpdateChatFukumaruRequest; id: number }) =>
        ChatFukumaruService.update(payload.body, payload.id),
      {
        onSuccess: () => {
          notificationMessage({
            message:
              SUCCESS_MESSAGE[
              SUCCESS.UPDATE_CHAT_FUKUMARU as keyof typeof SUCCESS_MESSAGE
              ],
            type: NOTIFY_STATUS.SUCCESS,
          });
          refetch();
          handleCancel();
          setParams({ ...params, page: 1 });
        },
        onError: (error: IResponseError) => {
          handleErrorCommon(error, setError);

          if (error.error === ERROR.CHAT_FUKUMARU_NOT_FOUND) {
            setOpenDrawer(false);
            refetch();
          }
        },
      },
    );

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ ...params, page, limit: pageSize });
  };

  const { mutate: deleteChatFukumaru, isLoading: isLoadingDelete } =
    useMutation(
      (id: number) => ChatFukumaruService.delete(id),
      {
        onSuccess: () => {
          notificationMessage({
            message:
              SUCCESS_MESSAGE[
              SUCCESS.DELETE_CHAT_FUKUMARU as keyof typeof SUCCESS_MESSAGE
              ],
            type: NOTIFY_STATUS.SUCCESS,
          });
          refetch();
          handleCancel();
          setParams({ ...params, page: 1 });
        },
        onError: (error: IResponseError) => {
          handleErrorCommon(error, setError);

          if (error.error === ERROR.CHAT_FUKUMARU_RELOAD_DATA) {
            setOpenDrawer(false);
            refetch();
          }
        },
      },
    );

  const columns: TableColumnsType = [
    {
      title: 'タイトル',
      dataIndex: 'title',
      width: '500px',
    },
    {
      title: '発信日時',
      render: (value, record) => {
        return moment(record.startAt).format('YYYY年MM月DD日 HH:mm');
      },
    },
    {
      title: '配信ステータス',
      width: '200px',
      render: (value, record) => {
        if (!record.status) {
          return notificationStatus[0].label;
        }

        return !record?.publicAt
          ? '配信予定'
          : '配信済み';
      },
    },
  ];

  const isDisabled = watch('isDisabled');

  useEffect(() => {
    setIsNotPermissionEdit(isDisabled);

    if (isNotFoundDetail) {
      setOpenDrawer(false);
      refetch();
    }
  }, [isDisabled, isNotFoundDetail]);

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header className='text-right'>
          <Button
            type='primary'
            onClick={() => {
              setOpenDrawer(true);
              setIsNotFoundDetail(false);
              reset({
                status: Status.PUBLIC,
              });
            }}
          >
            トーク発信新規作成
          </Button>
        </header>

        <div className='flex justify-between flex-col h-full'>
          <Table
            loading={isFetching || isLoadingUpload}
            scroll={{ x: 800 }}
            bordered
            rowKey='id'
            columns={columns}
            dataSource={data?.data.data}
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                setOpenDrawer(true);
                setCurrentId(record.id);
                setIsNotFoundDetail(false);
              },
            })}
            locale={{
              emptyText: 'トーク - フクマルはまだありません',
            }}
          />

          {data?.data?.pagination?.total! > 10 && (
            <Pagination
              className='pagination-table'
              total={data?.data.pagination.total}
              pageSize={params.limit}
              current={params.page}
              showSizeChanger={false}
              onChange={onChangePagination}
            />
          )}
        </div>
      </div>

      <Drawer
        title={
          !currentId ? 'トーク - フクマル新規登録' : 'トーク - フクマル詳細'
        }
        onOk={handleSubmit(currentId ? handleUpdate : handleCreate)}
        onCancel={handleCancel}
        onDelete={() => handleDelete(detail!)}
        maskClosable={false}
        open={openDrawer}
        okText={!currentId ? '保存 ' : '更新 '}
        loading={
          currentId
            ? isLoadingUpdate ||
            isLoadingDelete ||
            isFetchingDetail ||
            isLoadingUpload
            : isLoadingCreate || isLoadingUpload
        }
        isDeleted={!!currentId}
        allowDelete
        disabled={isDisabled}
      >
        <Form
          {...methods}
          layout='vertical'
          onSubmit={currentId ? handleUpdate : handleCreate}
        >
          <ChatFukumaruForm
            isLoading={
              isFetchingDetail ||
              isLoadingUpdate ||
              isLoadingCreate ||
              isLoadingDelete
            }
            openDrawer={openDrawer}
            chatFukumaru={detail}
          />
        </Form>
      </Drawer>
    </div>
  );
};

